/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { DocumentsListTableData } from '../../components/RegistryWidgets/CashRegisterModels/CashRegisterModelCard/CashRegisterModelTabs/DocumentsList/DocumentsListConfig';
import {
    CashRegisterModelDetailsDto,
    VersionWithCheckSums,
    SoftwareVersionsHistory,
    ModelDocumentsListDto,
    FactoryNumberTypes,
    FactoryNumbersListDto,
    FactoryNumbersListFilterParams,
} from '../api/cashRegisterModels/cashRegisterModelsClient.typings';
import {
    CashRegisterModelDetailsAction,
    CashRegisterModelDetailsActionType,
} from '../actions/cashRegisterModelsDetailsActions';
import { Order, PageQueryParams, SortQueryParamsGeneric } from '../api/apiCommon.typings';

export const docsRowsPerPage = [20, 30, 40, 50];
export const defaultDocsPaginationLimit = docsRowsPerPage[0];
export const defaultDocsSortingKey: keyof DocumentsListTableData = 'name';
export const defaultDocsSortingOrder: Order = 'asc';

export const versionsRowsPerPage = [15, 20, 25];
export const defaultVersionsPaginationLimit = versionsRowsPerPage[0];

export const checksumsRowsPerPage = [5, 10, 15];
export const defaultChecksumsPaginationLimit = checksumsRowsPerPage[0];

export const factoryNumbersRowsPerPage = [15, 20, 25, 30];
export const defaultFactoryNumbersPaginationLimit = factoryNumbersRowsPerPage[0];
export const defaultFactoryNumber = '';
export const defaultFactoryNumberType: FactoryNumberTypes = 'ALL';

export interface CashRegisterModelDetailsState {
    cashRegisterModelTaxNumber: string;
    cashRegisterModel: CashRegisterModelDetailsDto;
    versionsHistory: SoftwareVersionsHistory;
    versionsPageParams: PageQueryParams;
    documentsList: ModelDocumentsListDto;
    docsSortParams: SortQueryParamsGeneric<keyof DocumentsListTableData>;
    docsPageParams: PageQueryParams;
    factoryNumbersList: FactoryNumbersListDto;
    factoryNumbersListPageParams: PageQueryParams;
    factoryNumbersListFilterParams: FactoryNumbersListFilterParams;
    isLoading: boolean;
    isLoadingStartAttestation: boolean;
    isLoadingCompleteAttestation: boolean;
    isLoadingVersionsHistory: boolean;
    isLoadingDocumentsList: boolean;
    isLoadingFactoryNumbersList: boolean;
    currentInfoTab: number;
}

const initialState: CashRegisterModelDetailsState = {
    cashRegisterModelTaxNumber: null,
    cashRegisterModel: null,
    versionsHistory: null,
    versionsPageParams: {
        limit: defaultVersionsPaginationLimit,
        offset: 0,
    },
    documentsList: null,
    docsPageParams: {
        limit: defaultDocsPaginationLimit,
        offset: 0,
    },
    docsSortParams: {
        sortType: defaultDocsSortingKey,
        sortOrder: defaultDocsSortingOrder,
    },
    factoryNumbersList: null,
    factoryNumbersListPageParams: {
        limit: defaultFactoryNumbersPaginationLimit,
        offset: 0,
    },
    factoryNumbersListFilterParams: {
        factoryNumber: defaultFactoryNumber,
        used: defaultFactoryNumberType,
    },
    isLoading: false,
    isLoadingStartAttestation: false,
    isLoadingCompleteAttestation: false,
    isLoadingVersionsHistory: false,
    isLoadingDocumentsList: false,
    isLoadingFactoryNumbersList: false,
    currentInfoTab: 0,
};

export function cashRegisterModelDetailsReducer(
    state: CashRegisterModelDetailsState = initialState,
    action: CashRegisterModelDetailsAction,
): CashRegisterModelDetailsState {
    switch (action.type) {
        case CashRegisterModelDetailsActionType.OPEN_REGISTRY_CASH_REGISTER_MODEL_CARD_PAGE:
            const previousTaxNumber = state.cashRegisterModelTaxNumber;
            const currentTaxNumber = action.payload;

            let isNewCardOpen = true;
            if (previousTaxNumber && currentTaxNumber) isNewCardOpen = previousTaxNumber !== currentTaxNumber;
            return {
                ...state,
                isLoading: true,
                cashRegisterModelTaxNumber: action.payload,
                docsPageParams: initialState.docsPageParams,
                docsSortParams: initialState.docsSortParams,
                documentsList: initialState.documentsList,
                versionsHistory: initialState.versionsHistory,
                versionsPageParams: initialState.versionsPageParams,
                factoryNumbersList: initialState.factoryNumbersList,
                factoryNumbersListPageParams: isNewCardOpen
                    ? initialState.factoryNumbersListPageParams
                    : { ...state.factoryNumbersListPageParams },
                factoryNumbersListFilterParams: isNewCardOpen
                    ? initialState.factoryNumbersListFilterParams
                    : { ...state.factoryNumbersListFilterParams },
            };

        case CashRegisterModelDetailsActionType.FETCH_CASH_REGISTER_MODEL_DETAILS_SUCCESS:
            return {
                ...state,
                isLoading: false,
                cashRegisterModel: action.payload,
            };

        case CashRegisterModelDetailsActionType.FETCH_MODEL_DOCUMENTS_START:
            return {
                ...state,
                isLoadingDocumentsList: true,
            };

        case CashRegisterModelDetailsActionType.FETCH_MODEL_DOCUMENTS_SUCCESS:
            return {
                ...state,
                documentsList: action.payload,
                isLoadingDocumentsList: false,
            };

        case CashRegisterModelDetailsActionType.SUBMIT_DOCUMENTS_SUCCESS:
            return {
                ...state,
                isLoadingDocumentsList: true,
            };

        case CashRegisterModelDetailsActionType.DELETE_DOCUMENTS_SUCCESS:
        case CashRegisterModelDetailsActionType.DELETE_DOCUMENTS_ERROR: //some files might be deleted
            return {
                ...state,
                isLoadingDocumentsList: true,
            };

        case CashRegisterModelDetailsActionType.APPLY_DOCUMENTS_SORTING:
            return {
                ...state,
                docsSortParams: action.payload,
                isLoadingDocumentsList: true,
            };

        case CashRegisterModelDetailsActionType.APPLY_DOCUMENTS_PAGING:
            return {
                ...state,
                docsPageParams: action.payload,
                isLoadingDocumentsList: true,
            };

        case CashRegisterModelDetailsActionType.UPDATE_CASH_REGISTER_MODEL_DETAILS_SUCCESS:
            return {
                ...state,
                isLoading: true, //for details data reloading
            };

        case CashRegisterModelDetailsActionType.START_ATTESTATION:
            return {
                ...state,
                isLoadingStartAttestation: true,
            };

        case CashRegisterModelDetailsActionType.START_ATTESTATION_SUCCESS:
            return {
                ...state,
                isLoadingStartAttestation: false,
                isLoading: true, //for details data reloading
                isLoadingVersionsHistory: true, //for versions list reloading
                isLoadingDocumentsList: true, //for documents list reloading, docs can be added
                versionsPageParams: initialState.versionsPageParams,
            };

        case CashRegisterModelDetailsActionType.COMPLETE_ATTESTATION:
            return {
                ...state,
                isLoadingCompleteAttestation: true,
            };

        case CashRegisterModelDetailsActionType.COMPLETE_ATTESTATION_SUCCESS:
            return {
                ...state,
                isLoadingCompleteAttestation: false,
                isLoading: true, //for details data reloading
                isLoadingVersionsHistory: true, //for versions list reloading
                versionsPageParams: initialState.versionsPageParams,
            };

        case CashRegisterModelDetailsActionType.FETCH_SOFTWARE_VERSIONS_PAGE_START:
        case CashRegisterModelDetailsActionType.FETCH_SOFTWARE_VERSIONS_FULL_LIST_START:
            return {
                ...state,
                isLoadingVersionsHistory: true,
            };

        case CashRegisterModelDetailsActionType.FETCH_SOFTWARE_VERSIONS_SUCCESS:
            return {
                ...state,
                versionsHistory: action.payload,
                isLoadingVersionsHistory: false,
            };

        case CashRegisterModelDetailsActionType.APPLY_VERSIONS_PAGING:
            return {
                ...state,
                versionsPageParams: action.payload,
            };

        case CashRegisterModelDetailsActionType.FETCH_VERSION_CHECK_SUMS_START:
            return {
                ...state,
                isLoadingVersionsHistory: true,
            };

        case CashRegisterModelDetailsActionType.FETCH_VERSION_CHECK_SUMS_SUCCESS: {
            if (state.versionsHistory !== null) {
                const updatedVersions: VersionWithCheckSums[] = [...state.versionsHistory.versions];
                for (let i = 0; i < updatedVersions.length; i++) {
                    if (updatedVersions[i].id === action.payload.versionId) {
                        updatedVersions[i].checksumHistory = action.payload.checkSumsHistory;
                        break;
                    }
                }
                return {
                    ...state,
                    versionsHistory: {
                        count: state.versionsHistory.count,
                        versions: updatedVersions,
                    },
                    isLoadingVersionsHistory: false,
                };
            } else return state;
        }

        case CashRegisterModelDetailsActionType.CHANGE_VERSION_VIEW: {
            const versions = [...state.versionsHistory.versions];
            const count = state.versionsHistory.count;
            const itemIndex = versions.findIndex((item) => item.id === action.payload.versionId);
            if (itemIndex !== -1) {
                versions[itemIndex].isExpanded = action.payload.isExpanded;
                if (!action.payload.isExpanded) versions[itemIndex].checksumHistory = null;
            }
            return {
                ...state,
                versionsHistory: { count, versions },
            };
        }

        case CashRegisterModelDetailsActionType.COLLAPSE_ALL_VERSIONS_VIEWS: {
            const versions = [...state.versionsHistory.versions];
            const count = state.versionsHistory.count;
            for (let i = 0; i < versions.length; i++) {
                versions[i].isExpanded = false;
                versions[i].checksumHistory = null;
            }
            return {
                ...state,
                versionsHistory: { count, versions },
            };
        }

        case CashRegisterModelDetailsActionType.ACTIVATE_MODEL_SUCCESS:
        case CashRegisterModelDetailsActionType.ELIMINATE_MODEL_SUCCESS:
            return {
                ...state,
                isLoading: true, //for details data reloading
            };

        case CashRegisterModelDetailsActionType.ACTIVATE_VERSION_SUCCESS:
        case CashRegisterModelDetailsActionType.MARK_VERSION_OUTDATED_SUCCESS:
            return {
                ...state,
                isLoadingVersionsHistory: true, //for versions list reloading
            };

        case CashRegisterModelDetailsActionType.FETCH_FACTORY_NUM_START:
            return {
                ...state,
                isLoadingFactoryNumbersList: true,
            };

        case CashRegisterModelDetailsActionType.FETCH_FACTORY_NUM_SUCCESS:
            return {
                ...state,
                isLoadingFactoryNumbersList: false,
                factoryNumbersList: action.payload,
            };

        case CashRegisterModelDetailsActionType.CHANGE_FACTORY_NUM_PAGING:
            return {
                ...state,
                isLoadingFactoryNumbersList: true,
                factoryNumbersListPageParams: action.payload,
            };

        case CashRegisterModelDetailsActionType.CHANGE_FACTORY_NUM_FILTER:
            return {
                ...state,
                isLoadingFactoryNumbersList: true,
                factoryNumbersListFilterParams: action.payload,
                factoryNumbersListPageParams: { ...state.factoryNumbersListPageParams, offset: 0 },
            };

        case CashRegisterModelDetailsActionType.FETCH_CASH_REGISTER_MODEL_CARD_DATA_ERROR:
            return {
                ...state,
                isLoading: false,
                isLoadingStartAttestation: false,
                isLoadingCompleteAttestation: false,
                isLoadingVersionsHistory: false,
                isLoadingFactoryNumbersList: false,
                isLoadingDocumentsList: false,
                factoryNumbersList: {
                    count: 0,
                    cashRegisters: [],
                },
            };

        case CashRegisterModelDetailsActionType.EXIT_REGISTRY_CASH_REGISTER_MODEL_CARD_PAGE:
            return {
                ...state,
                cashRegisterModel: null,
                versionsHistory: null,
                currentInfoTab: 0,
            };

        case CashRegisterModelDetailsActionType.CHANGE_INFO_TAB:
            return {
                ...state,
                currentInfoTab: action.payload,
            };

        default:
            break;
    }

    return state;
}
